import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Input, Button, Row, Col, message, Upload, Select, Space } from 'antd';
import { 
  UserOutlined, 
  IdcardOutlined, 
  PhoneOutlined, 
  HomeOutlined, 
  BankOutlined, 
  TeamOutlined,
  LoadingOutlined,
  PlusOutlined, 
  BookOutlined
} from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import { jwtDecode } from "jwt-decode";

const { Option } = Select;

const EditStudentModal = ({ isOpen, onClose, onSave, studentId }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [, setLoading] = useState(true);
    const [, setFileList] = useState([]);    
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [uploadLoading, setUploadLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [branches, setBranches] = useState([]);
    const API_URL = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");
        let userRole = null;
        let userBranchId = null;

        if (token) {
        try {
            const decoded = jwtDecode(token);
            userRole = decoded.role;
            userBranchId = decoded.branch_id || null;
        } catch (err) {
            console.error("Invalid token", err);
        }
        }

    useEffect(() => {
    if (!isOpen) {
        form.resetFields();
        setImageUrl('');
        setPreviewImage(''); // Reset preview
        setDateOfBirth('');
        setFileList([]);
        setLoading(true);
    }
}, [isOpen, form]);

useEffect(() => {
  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API_URL}/branch`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBranches(res.data);
    } catch (err) {
      console.error("Failed to fetch branches", err);
    }
  };

  if (userRole === "superadmin" || userRole === "admin") {
    fetchBranches();
  }
}, [userRole, token]);

    useEffect(() => {
        // Only fetch if the modal is open and we have a student ID
        if (isOpen && studentId) {
            const fetchStudent = async () => {
                // Reset image URL at the start of fetch to prevent showing old image
                setImageUrl('');
                
                try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(`${API_URL}/students/${studentId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
    
                    const studentData = response.data;
    
                    // Format date of birth properly for the input field
                    let formattedDateOfBirth = '';
                    if (studentData.date_of_birth) {
                        // Convert to YYYY-MM-DD format
                        const date = new Date(studentData.date_of_birth);
                        formattedDateOfBirth = date.toISOString().split('T')[0];
                        setDateOfBirth(formattedDateOfBirth);
                    }
                    
                    // Set all form fields except date of birth which is handled separately
                    form.setFieldsValue({
                        ...studentData,
                        date_of_birth: undefined // Clear this to avoid conflict with our custom handler
                    });
    
                    if (studentData.nationalIdPdf) {
                        setFileList([
                            {
                                uid: '-1',
                                name: 'Uploaded File',
                                status: 'done',
                                url: studentData.nationalIdPdf,
                            }
                        ]);
                    }
    
                    // Set profile photo if available
                   // Set profile photo if available
                if (studentData.profile_photo) {
                    // Extract base URL (remove the API path part)
                    const baseUrl = API_URL.replace(/\/api.*$/, '');
                    
                    // Handle the case where profile_photo contains /var/www/uploads
                    if (studentData.profile_photo.includes('/var/www/uploads')) {
                        // Replace /var/www with nothing to get the correct URL path
                        const fixedPath = studentData.profile_photo.replace('/var/www', '');
                        setImageUrl(`${baseUrl}${fixedPath}`);
                    } else if (studentData.profile_photo.startsWith('/uploads')) {
                        // Already a relative path
                        setImageUrl(`${baseUrl}${studentData.profile_photo}`);
                    } else if (studentData.profile_photo.startsWith('http')) {
                        // If it's already a full URL, use it directly
                        setImageUrl(studentData.profile_photo);
                    } else {
                        // For any other format, append to base URL
                        setImageUrl(`${baseUrl}/${studentData.profile_photo}`);
                    }
                    
                }
                } catch (error) {
                    console.error('Error fetching student:', error);
                    message.error(t('fetchStudentError'));
                } finally {
                    setLoading(false);
                }
            };
    
            fetchStudent();
        }
    }, [studentId, API_URL, form, t, isOpen]);

    
const beforeUpload = async (file) => {
  const isValidType = file.type === 'image/jpeg' || file.type === 'image/png';
  const isValidSize = file.size / 1024 / 1024 < 2;

  if (!isValidType) {
    message.error('You can only upload JPG/PNG file!');
    return false;
  }

  if (!isValidSize) {
    message.error('Image must be smaller than 2MB!');
    return false;
  }

  // Preview image
  const reader = new FileReader();
  reader.onload = (e) => {
    setPreviewImage(e.target.result);
  };
  reader.readAsDataURL(file);

  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('profilePhoto', file);
    formData.append('studentId', studentId); 

    const res = await axios.post(`${API_URL}/image-upload/profile-photo`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    if (res.data?.url) {
      form.setFieldsValue({ profile_photo_url: res.data.url });
      message.success('Uploaded successfully');
    } else {
      throw new Error('No URL returned from server');
    }
  } catch (err) {
    console.error('Upload error:', err);
    message.error('Failed to upload image');
  }

  return false;
};

const handleProfilePhotoChange = ({ file }) => {
  if (file.status === 'uploading') {
    setUploadLoading(true);
    return;
  }

  if (file.status === 'done' || file.status === 'error') {
    setUploadLoading(false);
  }

  if (file.originFileObj) {
    form.setFieldsValue({
      profilePhoto: {
        fileList: [file]
      }
    });
  }
};

const handleSubmit = async (values) => {
  try {
    const token = localStorage.getItem('token');

    const payload = {
      ...values,
      date_of_birth: dateOfBirth || null, // Allow null if dateOfBirth is empty/undefined
      schoolid: values.schoolid ? parseInt(values.schoolid) : '',
      profile_photo_url: values.profile_photo_url || null,
      branch_id: values.branch_id || null,
    };

    const res = await axios.put(`${API_URL}/students/${studentId}`, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });

    message.success(t('studentUpdatedSuccessfully'));

    if (onSave) onSave(res.data);
    onClose();
  } catch (err) {
    console.error('Submit error:', err);
    message.error(t('errorUpdatingStudent'));
  }
};

    // Custom upload button for profile photo
    const uploadButton = (
        <div>
            {uploadLoading ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 8 }}>{t('upload')}</div>
        </div>
    );
    
    return (
        <Modal
            title={t('editStudent')}
            open={isOpen}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>{t('cancel')}</Button>,
                <Button key="submit" type="primary" onClick={() => form.submit()}>{t('save')}</Button>,
            ]}
            width={800}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{}}
            >
                {/* Profile Photo Upload Section - At the top */}
                <Row justify="center" style={{ marginBottom: 24 }}>
                    <Col>
                        <div style={{ textAlign: 'center' }}>
                            <Form.Item 
                                name="profilePhoto" 
                                valuePropName="file"
                                noStyle
                            >
                                <Upload
                                    name="profilePhoto"
                                    listType="picture-card"
                                    className="avatar-uploader"
                                    showUploadList={false}
                                    beforeUpload={beforeUpload}
                                    onChange={handleProfilePhotoChange}
                                    maxCount={1}
                                    accept=".jpg,.jpeg,.png"
                                >
                                    {(previewImage || imageUrl) ? (
                                        <div style={{ position: 'relative', width: '100px', height: '100px' }}>
                                            <img 
                                                src={previewImage || imageUrl} 
                                                alt="Profile" 
                                                style={{ 
                                                    width: '100%', 
                                                    height: '100%', 
                                                    objectFit: 'cover',
                                                    borderRadius: '8px'
                                                }} 
                                            />
                                            {previewImage && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '5px',
                                                    right: '5px',
                                                    background: 'rgba(0,0,0,0.6)',
                                                    color: 'white',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    fontSize: '10px'
                                                }}>
                                                    New
                                                </div>
                                            )}
                                        </div>
                                    ) : uploadButton}
                                </Upload>
                            </Form.Item>
                            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                {t('Click to upload a new photo') || 'Click to upload profile photo'}
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                        name="branch_id"
                        label={t("Branch")}
                        rules={[{ required: true, message: t("form.branch_required") }]}
                        >
                        <Select
                            placeholder={t("form.select_branch")}
                            disabled={userRole !== "superadmin"} // 🔒 disable if not superadmin
                        >
                            {branches.map((branch) => (
                            <Option key={branch.id} value={branch.id}>
                                {branch.name}
                            </Option>
                            ))}
                        </Select>
                        </Form.Item>
                    </Col>
                    </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="firstname"
                            label={t('firstName')}
                            rules={[{ required: true, message: t('form.required_field') }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder={t('firstName')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="lastname"
                            label={t('lastName')}
                            rules={[{ required: true, message: t('form.required_field') }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder={t('lastName')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="nickname"
                            label={t('nickname')}
                        >
                            <Input placeholder={t('nickname')} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="gender"
                            label={t('gender')}
                            rules={[{ required: false }]}
                        >
                            <Select placeholder={t('selectGender')}>
                                <Option value="male">{t('male')}</Option>
                                <Option value="female">{t('female')}</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="email"
                            label={t('email')}
                            rules={[{ required: true, type: 'email', message: t('form.invalid_email') }]}
                        >
                            <Input placeholder={t('email')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label={t('dateOfBirth')}
                        >
                            <input
                                type="date"
                                value={dateOfBirth}  
                                onChange={(e) => setDateOfBirth(e.target.value)}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    border: "1px solid #d9d9d9",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="nationalid"
                            label={t('nationalID')}
                        >
                            <Input prefix={<IdcardOutlined />} placeholder={t('nationalID')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="phone"
                            label={t('phone')}
                        >
                            <Input prefix={<PhoneOutlined />} placeholder={t('phone')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="address"
                            label={t('address')}
                        >
                            <Input prefix={<HomeOutlined />} placeholder={t('address')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item 
                  name="currentEducationLevel"
                  label={<Space><BookOutlined /> {t('form.current_education_level')}</Space>}
                  rules={[{ required: false, message: t('form.required_field') }]}
                >
                  <Select
                    placeholder={t('Select Education Level')}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {/* Year 1–12 */}
                    <Select.OptGroup label="Year Levels">
                      {Array.from({ length: 13 }, (_, i) => (
                        <Select.Option key={`Year ${i+1}`} value={`Year ${i+1}`}>
                          {`Year ${i+1}`}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>

                    {/* Grade 1–12 */}
                    <Select.OptGroup label="Grade Levels">
                      {Array.from({ length: 12 }, (_, i) => (
                        <Select.Option key={`Grade ${i+1}`} value={`Grade ${i+1}`}>
                          {`Grade ${i+1}`}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>

                    {/* University & Adults */}
                    <Select.OptGroup label="Other">
                      <Select.Option value="University">{t('University')}</Select.Option>
                      <Select.Option value="Adults">{t('Adults')}</Select.Option>
                    </Select.OptGroup>
                  </Select>
                </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="schoolname"
                            label={t('schoolName')}
                        >
                            <Input prefix={<BankOutlined />} placeholder={t('schoolName')} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="guardianname"
                            label={t('guardianName')}
                        >
                            <Input prefix={<TeamOutlined />} placeholder={t('guardianName')} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="guardiancontact"
                            label={t('guardianContact')}
                        >
                            <Input prefix={<PhoneOutlined />} placeholder={t('guardianContact')} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default withRoleAccess(EditStudentModal, ['admin', 'superadmin']);